import { Editor } from '@tiptap/react';
import type { RicosEditorProps } from 'ricos-common';
import type { ExtensionProps, RicosServices } from 'ricos-types';

import { RichContentAdapter } from './components/RichContentAdapter';
import { applyDevTools } from './components/RicosTiptapEditor/apply-dev-tools';
import { coreConfigs } from './components/RicosTiptapEditor/core-configs';
import { Extensions } from './models/Extensions';
import { patchExtensions } from './patch-extensions';
import { transformPastedHTML } from './transform-pasted-html';

export type TiptapAdapterServices = Omit<RicosServices, 'editor' | 'zIndexService'>;

const extractExtensionProps = (props: RicosEditorProps): ExtensionProps => {
  const {
    placeholder,
    textAlignment,
    iframeSandboxDomain,
    textWrap,
    maxTextLength,
    linkSettings,
    theme,
  } = props;

  const { anchorTarget, rel, relValue } = linkSettings || {};
  return {
    placeholder,
    textAlignment,
    iframeSandboxDomain,
    isTextWrap: textWrap,
    maxTextLength,
    anchorTarget,
    rel,
    relValue,
    breakoutRule: theme?.breakout?.rule,
  };
};

const extractExtensions = (
  services: TiptapAdapterServices,
  props: RicosEditorProps
): Extensions => {
  const extensions = services.plugins.getTiptapExtensions();
  return Extensions.of(extensions, extractExtensionProps(props), services);
};

export const initializeTiptapAdapter = (
  ricosEditorProps: RicosEditorProps,
  services: TiptapAdapterServices,
  isDebugMode = false
) => {
  const tiptapContent = services.schema.getTiptapContent();
  const extensions = extractExtensions(services, ricosEditorProps);
  const allExtensions = extensions.concat(coreConfigs);
  const patchedExtensions = patchExtensions(tiptapContent, allExtensions);
  const tiptapExtensions = patchedExtensions.getTiptapExtensions();

  const { autofocus } = ricosEditorProps;

  const editor = new Editor({
    extensions: tiptapExtensions,
    content: tiptapContent,
    injectCSS: true,
    editorProps: {
      transformPastedHTML,
    },
    onCreate({ editor }) {
      if (ricosEditorProps.onLoad) {
        ricosEditorProps.onLoad({ editor });
      }
    },
    ...(autofocus && { autofocus }),
  });

  applyDevTools(editor, isDebugMode);

  return new RichContentAdapter(editor, services);
};
